import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, LinearProgress, MenuItem, Select, Stack,
  TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import useUser from '../../../../hooks/useUser';
import { GargiOpportunity } from '../../interfaces/gargi';
import {
  assignOpportunity, audiencePath, buildAudience, getTicketGroups, ignoreOpportunity, launchOpportunity, previewOpportunity,
} from '../../services/gargi';
import { formatInr } from '../../utils/format';
import { count } from '../Models/common';
import { actionError } from '../../../../shared/gargi/actionError';

export type ActionKind = 'launch' | 'audience' | 'assign' | 'ignore';
export type Pending = { kind: ActionKind; opportunity: GargiOpportunity } | null;

const TITLE: Record<ActionKind, string> = {
  launch: 'Launch a journey',
  audience: 'Save as an audience',
  assign: 'Assign to a team',
  ignore: 'Set this aside',
};

const SUBMIT: Record<ActionKind, string> = {
  launch: 'Open the journey draft',
  audience: 'Save audience',
  assign: 'Assign',
  ignore: 'Set aside',
};

const EXPLAIN: Record<ActionKind, string> = {
  launch: 'Opens a journey draft for these customers. 1 in 10 is held back as a control group and gets no message, so the results show what the journey added beyond what they would have bought anyway. Add your message, then publish; results appear under Results.',
  audience: 'Saves these customers as a cohort you can pick in any journey. It keeps the rule, not a fixed list, so each run reaches the customers who match on that day.',
  assign: 'This creates a ticket for the team with a "Create journey" link. Opening it drafts a journey for this audience with a control group; publishing the journey closes the ticket, and results appear under Results.',
  ignore: 'It leaves your list for 30 days and stays recoverable under Results → Set aside.',
};

// One dialog for every action on an opportunity card, wherever the card is shown.
export const ActDialog: FC<{ pending: Pending; onClose: () => void }> = ({ pending, onClose }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { selectedBusiness } = useUser();
  const [group, setGroup] = useState<number | ''>('');
  const [priority, setPriority] = useState<'low' | 'medium' | 'high'>('medium');
  const [notes, setNotes] = useState('');
  const [name, setName] = useState('');
  const [saved, setSaved] = useState<{ cohortId: string; name: string } | null>(null);
  const groups = useQuery({ queryKey: ['ticket-groups', selectedBusiness?.id], queryFn: getTicketGroups, enabled: pending?.kind === 'assign' });
  // A card from an existing screen has no count of its own: count its audience live before acting.
  const needsCount = !!pending && pending.kind !== 'ignore' && pending.opportunity.customerCount == null;
  const preview = useQuery({
    queryKey: ['opportunity-preview', selectedBusiness?.id, pending?.opportunity.id, pending?.opportunity.metadata],
    queryFn: () => previewOpportunity(pending!.opportunity),
    enabled: needsCount,
    staleTime: 10 * 60 * 1000,
  });
  const counted = pending && {
    ...pending.opportunity,
    customerCount: pending.opportunity.customerCount ?? preview.data?.customerCount ?? null,
    atv: pending.opportunity.atv ?? preview.data?.atv ?? null,
  };

  useEffect(() => {
    setGroup(''); setNotes(''); setPriority('medium'); setSaved(null); setName(pending?.opportunity.name ?? '');
  }, [pending]);

  const mutation = useMutation({
    mutationFn: async () => {
      const o = counted!;
      switch (pending!.kind) {
        case 'assign': return assignOpportunity(o, Number(group), priority, notes || undefined);
        case 'ignore': return ignoreOpportunity(o, notes || undefined);
        case 'launch': return launchOpportunity(o);
        case 'audience': return buildAudience(o, name.trim() || undefined);
      }
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['gargi-opportunities'] });
      queryClient.invalidateQueries({ queryKey: ['gargi-interactions'] });
      if (pending!.kind === 'launch') {
        navigate(`/journeys/from-assignment/${(result as { id: string }).id}`);
        return;
      }
      if (pending!.kind === 'audience') {
        setSaved(result as { cohortId: string; name: string });
        return;
      }
      onClose();
    },
  });

  if (!pending) return null;
  const { kind } = pending;
  const o = counted!;
  const blocked = mutation.isPending || (needsCount && !preview.data) || (kind === 'assign' && !group) || (kind === 'audience' && !name.trim());

  return (
    <Dialog open onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{TITLE[kind]}</DialogTitle>
      <DialogContent>
        <Typography variant='subtitle2' fontWeight={700}>{o.name}</Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          {needsCount && preview.isLoading ? 'Counting customers…' : `${count(o.customerCount)} customers`}
          {o.predictedRevenue ? ` · ${formatInr(o.predictedRevenue)} potential` : ''}
        </Typography>
        {needsCount && preview.isError && (
          <Alert severity='warning' sx={{ mb: 2 }}>This audience could not be counted, so it can&apos;t be used yet.</Alert>
        )}
        {kind === 'assign' && (
          <Stack spacing={2}>
            {groups.isLoading && <LinearProgress />}
            {groups.isError && <Alert severity='error'>Ticket groups could not be loaded.</Alert>}
            {groups.data && !groups.data.length && (
              <Alert severity='info'>No ticket groups are set up for this business yet. Add one in Ticket Desk to assign opportunities.</Alert>
            )}
            {!!groups.data?.length && (
              <FormControl size='small' fullWidth>
                <InputLabel>Team</InputLabel>
                <Select label='Team' value={group} onChange={(e) => setGroup(e.target.value as number)}>
                  {groups.data.map((g) => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
                </Select>
              </FormControl>
            )}
            <ToggleButtonGroup size='small' exclusive value={priority} onChange={(_, v) => v && setPriority(v)}>
              {(['low', 'medium', 'high'] as const).map((p) => <ToggleButton key={p} value={p} sx={{ textTransform: 'capitalize', px: 2 }}>{p}</ToggleButton>)}
            </ToggleButtonGroup>
          </Stack>
        )}
        {kind === 'audience' && !saved && (
          <TextField fullWidth size='small' label='Audience name' value={name} onChange={(e) => setName(e.target.value)} inputProps={{ maxLength: 120 }} />
        )}
        {(kind === 'assign' || kind === 'ignore') && (
          <TextField fullWidth multiline minRows={2} size='small' sx={{ mt: 2 }} value={notes} onChange={(e) => setNotes(e.target.value)}
            label={kind === 'assign' ? 'Notes for the team (optional)' : 'Reason (optional)'} inputProps={{ maxLength: 1000 }} />
        )}
        {saved
          ? <Alert severity='success' sx={{ mt: 2 }}>Saved as “{saved.name}”. Pick it as the audience when you create a journey.</Alert>
          : <Alert severity='info' sx={{ mt: 2 }}>{EXPLAIN[kind]}</Alert>}
        {mutation.isError && <Alert severity='error' sx={{ mt: 2 }}>{actionError(mutation.error)}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {saved && <Button onClick={() => navigate(`/audiences/${saved.cohortId}`)}>Open audience</Button>}
        <Button onClick={onClose}>{saved ? 'Done' : 'Cancel'}</Button>
        {!saved && (
          <Button variant='contained' disableElevation disabled={blocked} onClick={() => mutation.mutate()}>{SUBMIT[kind]}</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// Builds the card's whole journey in one click (audience, 90/10 control, messages with the business's approved
// templates, waits, follow-ups only to those who haven't bought) and opens it in the builder with its Gargi thread.
export const CreateJourneyButton: FC<{ o: GargiOpportunity }> = ({ o }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const create = useMutation({
    mutationFn: () => launchOpportunity(o),
    onSuccess: ({ id }) => {
      queryClient.invalidateQueries({ queryKey: ['gargi-opportunities'] });
      queryClient.invalidateQueries({ queryKey: ['gargi-interactions'] });
      navigate(`/journeys/from-assignment/${id}`);
    },
  });
  return (
    <>
      <Button size='small' variant='contained' disableElevation disabled={create.isPending} onClick={() => create.mutate()}>
        {create.isPending ? 'Creating…' : 'Create journey'}
      </Button>
      {create.isError && (
        <Typography variant='caption' color='error' sx={{ width: '100%' }}>{actionError(create.error, 'The journey could not be created.')}</Typography>
      )}
    </>
  );
};

// The actions every opportunity card offers. Create journey comes first: it is the shortest path from idea to result.
// `onAssign` replaces the Ticket Desk assignment (Growth Desk gives the work an owner and a due date instead).
export const OpportunityButtons: FC<{ o: GargiOpportunity; onAct: (p: Pending) => void; canIgnore?: boolean; onAssign?: () => void }> = ({ o, onAct, canIgnore, onAssign }) => {
  const navigate = useNavigate();
  return (
  <>
    <CreateJourneyButton o={o} />
    <Button size='small' variant='outlined' onClick={() => onAct({ kind: 'audience', opportunity: o })}>Save audience</Button>
    <Button size='small' variant='outlined' onClick={() => (onAssign ? onAssign() : onAct({ kind: 'assign', opportunity: o }))}>Assign</Button>
    <Button size='small' variant='text' onClick={() => navigate(audiencePath(o.id, o.metadata ?? {}))}>Explore</Button>
    {canIgnore && <Button size='small' variant='text' onClick={() => onAct({ kind: 'ignore', opportunity: o })}>Set aside</Button>}
  </>
  );
};
